/**
 * `commentSurfaceBudget` — the COST BOUND on canvas comment bodies.
 *
 * ## The cost, measured rather than asserted
 *
 * Painting a comment body on the canvas engine (owner decision **D-E**) makes
 * every comment card a real `pl_layout` pass over a real docz block plus a real
 * canvas backing store. `web-page-layout`'s wasm module is a cached singleton, so
 * the 1.7 MB engine is loaded ONCE for the whole page — but the LAYOUT PASS is
 * per surface, and a long thread has as many surfaces as it has comments.
 *
 * `commentCanvasCost.test.ts` measures the real per-comment pass against the
 * real `pdfz-layout.wasm` and prints the arithmetic for a 40-comment thread.
 *
 * ## The two bounds
 *
 * 1. **VIRTUALISE** (the primary bound, in `<CommentCanvasBody>`): a card mounts
 *    its surface only once it is in (or near) the viewport, and tears it down
 *    when it leaves. A thread of any length therefore costs the VIEWPORT, not the
 *    thread. This is the choice reported to the owner: not batching (comment
 *    cards are interleaved with chrome — author, timestamp, reactions — so they
 *    cannot share one document surface) and not pooling a single surface (each
 *    card needs its own mount node).
 * 2. **CAP** (this module): a hard ceiling on concurrently live surfaces that
 *    holds even when virtualisation cannot help — a very tall display, a runtime
 *    with no `IntersectionObserver`, or a scroll that reveals everything at once.
 *
 * A card denied a slot is NOT blank and NOT silent: it presents the accessible
 * mirror VISIBLY (`MIRROR_VISIBLE_STYLE` — reflowed, readable, explicitly not a
 * layout-faithful rendition) until a slot frees. Scrolling releases slots, so the
 * denial self-heals.
 */

/** The ceiling on concurrently live canvas comment surfaces. More than this
 *  cannot fit in a review rail viewport, so a request beyond it is a runtime
 *  that lost its visibility signal, not a reader who needs 40 painted cards. */
export const MAX_LIVE_COMMENT_SURFACES = 12;

/** How far outside the viewport a comment card starts painting, so a scroll
 *  never reveals an unpainted card. One card height is plenty. */
export const COMMENT_SURFACE_OVERSCAN_CSS = 240;

export interface SurfaceBudget {
  /** Claim a slot for `key`. `true` when the caller may mount a surface.
   *  Idempotent for a key that already holds a slot. */
  admit(key: string): boolean;
  /** Give the slot back. Idempotent; an unknown key is a no-op. */
  release(key: string): void;
  /** Slots currently held. */
  live(): number;
  /** The HIGH-WATER mark — the most slots ever held at once. This is the number
   *  a cost report quotes; it must not decay when slots are released. */
  peakLive(): number;
  /** The keys currently holding slots, in admission order. */
  admitted(): readonly string[];
}

export function createSurfaceBudget(max: number): SurfaceBudget {
  const held = new Set<string>();
  let peak = 0;
  return {
    admit(key) {
      if (held.has(key)) return true;
      if (held.size >= max) return false;
      held.add(key);
      if (held.size > peak) peak = held.size;
      return true;
    },
    release(key) {
      held.delete(key);
    },
    live: () => held.size,
    peakLive: () => peak,
    admitted: () => [...held],
  };
}

/** The process-wide budget every comment card shares. One ceiling for the whole
 *  page: two threads open side by side must not each get the full allowance. */
export const commentSurfaceBudget: SurfaceBudget = createSurfaceBudget(
  MAX_LIVE_COMMENT_SURFACES,
);
